import { useEffect, useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { collection, onSnapshot, deleteDoc, doc } from 'firebase/firestore';
import {
  Box, Paper, Typography, Button, TextField, InputAdornment, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, Chip, IconButton, Stack,
  TableSortLabel
} from '@mui/material';
import { useMediaQuery, useTheme, Card, CardContent, CardActions, Divider } from '@mui/material';
import {
  Add, Search, Edit, Delete, FileDownload,
  Business, Phone, Mail, LocationOn, Language, Map
} from '@mui/icons-material';
import { db } from './firebase';
import { STAGES } from './constants';
import { exportContattiCSV, stageMeta } from './helpers';
import { useTags } from './useTags';
import ContactModal from './ContactModal';
import ContactDetail from './ContactDetail';

export default function Contatti() {
  const [contatti, setContatti] = useState([]);
  const [search, setSearch] = useState('');
  const [filterTag, setFilterTag] = useState('tutti');
  const [filterStage, setFilterStage] = useState('tutti');
  const [filterCitta, setFilterCitta] = useState('tutti');
  const [filterOwner, setFilterOwner] = useState('tutti');
  const [sortField, setSortField] = useState('nome');
  const [sortDir, setSortDir] = useState('asc');
  const [modal, setModal] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedId = searchParams.get('id');
  const { getColor } = useTags();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); 
  const getContactTags = (c) => {
    if (Array.isArray(c.tags) && c.tags.length) return c.tags.filter(Boolean);
    if (c.tag) return [c.tag];
    return [];
  };

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'crm_contatti'), s => {
      setContatti(s.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return unsub;
  }, []);

  const uniqueTags = useMemo(
    () => [...new Set(contatti.flatMap(c => getContactTags(c)))].sort(),
    [contatti]
  );

  const uniqueCitta = useMemo(
    () => [...new Set(contatti.map(c => c.citta).filter(Boolean))].sort(),
    [contatti]
  );

  const uniqueOwners = useMemo(
    () => [...new Set(contatti.map(c => c.owner).filter(Boolean))].sort(),
    [contatti]
  );

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const filtered = useMemo(() => {
    const q = search.toLowerCase();

    let result = contatti.filter(c => {
      const contactTags = getContactTags(c);

      if (filterTag !== 'tutti' && !contactTags.includes(filterTag)) return false;
      if (filterStage !== 'tutti' && c.stage !== filterStage) return false;
      if (filterCitta !== 'tutti' && c.citta !== filterCitta) return false;
      if (filterOwner !== 'tutti' && c.owner !== filterOwner) return false;

      const searchableText = (
        (c.nome || '') + ' ' +
        (c.azienda || '') + ' ' +
        (c.citta || '') + ' ' +
        (c.telefono || '') + ' ' +
        contactTags.join(' ')
      ).toLowerCase();

      if (q && !searchableText.includes(q)) return false;

      return true;
    });

    result.sort((a, b) => {
      const valA =
        sortField === 'tag'
          ? getContactTags(a).join(' ').toLowerCase()
          : (a[sortField] || '').toString().toLowerCase();

      const valB =
        sortField === 'tag'
          ? getContactTags(b).join(' ').toLowerCase()
          : (b[sortField] || '').toString().toLowerCase();

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [contatti, search, filterTag, filterStage, filterCitta, filterOwner, sortField, sortDir]);

  const selected = contatti.find(c => c.id === selectedId);

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo contatto?')) return;
    await deleteDoc(doc(db, 'crm_contatti', id));
    if (selectedId === id) setSearchParams({});
  };

  const columns = [
    { id: 'nome', label: 'Nome / Azienda' },
    { id: 'telefono', label: 'Contatto' },
    { id: 'citta', label: 'Città' },
    { id: 'tag', label: 'Tag' },
    { id: 'stage', label: 'Stage' },
    { id: 'owner', label: 'Owner' }
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>Contatti</Typography>
          <Typography variant="body2" color="text.secondary">
            {filtered.length} di {contatti.length}
          </Typography>
        </Box>

        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<FileDownload />}
            onClick={() => exportContattiCSV(contatti)}
            variant="outlined"
          >
            CSV
          </Button>

          <Button
            startIcon={<Add />}
            onClick={() => setModal({ stage: 'nuovo', tags: [] })}
            variant="contained"
          >
            Nuovo
          </Button>
        </Stack>
      </Box>

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Cerca..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ flex: 1, minWidth: 200 }}
        />

        <TextField
          select
          size="small"
          value={filterTag}
          onChange={e => setFilterTag(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="tutti">Tutti i tag</MenuItem>
          {uniqueTags.map(t => (
            <MenuItem key={t} value={t}>{t}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          value={filterStage}
          onChange={e => setFilterStage(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="tutti">Tutti gli stage</MenuItem>
          {STAGES.map(s => (
            <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          value={filterCitta}
          onChange={e => setFilterCitta(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="tutti">Tutte le città</MenuItem>
          {uniqueCitta.map(c => (
            <MenuItem key={c} value={c}>{c}</MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          value={filterOwner}
          onChange={e => setFilterOwner(e.target.value)}
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="tutti">Tutti gli owner</MenuItem>
          {uniqueOwners.map(o => (
            <MenuItem key={o} value={o}>{o}</MenuItem>
          ))}
        </TextField>
      </Paper>

      {isMobile ? (
  <Stack spacing={1}>
    {filtered.length === 0 ? (
      <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Nessun contatto.</Paper>
    ) : filtered.map(c => {
      const s = stageMeta(c.stage, STAGES);
      const contactTags = getContactTags(c);
      return (
        <Card key={c.id} variant="outlined" onClick={() => setSearchParams({ id: c.id })} sx={{ cursor: 'pointer' }}>
          <CardContent sx={{ pb: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }} noWrap>{c.nome}</Typography>
                {c.azienda && c.azienda !== c.nome && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Business sx={{ fontSize: 11 }} />{c.azienda}
                  </Typography>
                )}
              </Box>
              <Chip label={s.label} size="small" sx={{ bgcolor: s.color + '20', color: s.color, fontWeight: 600, ml: 1, flexShrink: 0 }} />
            </Box>
            <Stack direction="row" spacing={1.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
              {c.telefono && <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Phone sx={{ fontSize: 12 }} />{c.telefono}</Typography>}
              {c.citta && <Typography variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationOn sx={{ fontSize: 12 }} />{c.citta}</Typography>}
            </Stack>
            {contactTags.length > 0 && (
              <Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {contactTags.map(tag => <Chip key={tag} label={tag} size="small" sx={{ bgcolor: getColor(tag), color: 'white', fontWeight: 600, fontSize: 11 }} />)}
              </Stack>
            )}
          </CardContent>
          <Divider />
          <CardActions sx={{ justifyContent: 'space-between', py: 0.5, px: 1 }} onClick={e => e.stopPropagation()}>
            <Stack direction="row" spacing={0.5}>
              {c.telefono && <IconButton size="small" href={'tel:' + c.telefono}><Phone fontSize="small" color="primary" /></IconButton>}
              {c.googleMapsLink && <IconButton size="small" href={c.googleMapsLink} target="_blank"><Map fontSize="small" color="action" /></IconButton>}
            </Stack>
            <Stack direction="row" spacing={0.5}>
              <IconButton size="small" onClick={() => setModal(c)}><Edit fontSize="small" /></IconButton>
              <IconButton size="small" onClick={() => handleDelete(c.id)}><Delete fontSize="small" /></IconButton>
            </Stack>
          </CardActions>
        </Card>
      );
    })}
  </Stack>
) : (
  <Paper>
    <Table size="small">
      <TableHead>
        <TableRow>
          {columns.map(col => (
            <TableCell key={col.id}>
              <TableSortLabel
                active={sortField === col.id}
                direction={sortField === col.id ? sortDir : 'asc'}
                onClick={() => handleSort(col.id)}
              >
                {col.label}
              </TableSortLabel>
            </TableCell>
          ))}
          <TableCell align="right">Link</TableCell>
          <TableCell align="right"></TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {filtered.length === 0 ? (
          <TableRow>
            <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
              Nessun contatto.
            </TableCell>
          </TableRow>
        ) : (
          filtered.map(c => {
            const s = stageMeta(c.stage, STAGES);
            const contactTags = getContactTags(c);

            return (
              <TableRow
                key={c.id}
                hover
                onClick={() => setSearchParams({ id: c.id })}
                sx={{ cursor: 'pointer' }}
              >
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {c.nome}
                  </Typography>
                  {c.azienda && c.azienda !== c.nome && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Business sx={{ fontSize: 12 }} />
                      {c.azienda}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {c.telefono && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Phone sx={{ fontSize: 12 }} />
                      {c.telefono}
                    </Typography>
                  )}
                  {c.email && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <Mail sx={{ fontSize: 12 }} />
                      {c.email}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {c.citta && (
                    <Typography
                      variant="caption"
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <LocationOn sx={{ fontSize: 12 }} />
                      {c.citta}
                    </Typography>
                  )}
                </TableCell>

                <TableCell>
                  {contactTags.length ? (
                    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                      {contactTags.map(tagName => (
                        <Chip
                          key={tagName}
                          label={tagName}
                          size="small"
                          sx={{
                            bgcolor: getColor(tagName),
                            color: 'white',
                            fontWeight: 600
                          }}
                        />
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">—</Typography>
                  )}
                </TableCell>

                <TableCell>
                  <Chip
                    label={s.label}
                    size="small"
                    sx={{ bgcolor: s.color + '20', color: s.color, fontWeight: 600 }}
                  />
                </TableCell>

                <TableCell>
                  <Typography variant="caption">{c.owner}</Typography>
                </TableCell>

                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <Stack direction="row" spacing={0.5}>
                    {c.googleMapsLink && (
                      <IconButton size="small" href={c.googleMapsLink} target="_blank">
                        <Map fontSize="small" color="action" />
                      </IconButton>
                    )}
                    {c.sitoWeb && (
                      <IconButton
                        size="small"
                        href={c.sitoWeb.startsWith('http') ? c.sitoWeb : 'https://' + c.sitoWeb}
                        target="_blank"
                      >
                        <Language fontSize="small" color="action" />
                      </IconButton>
                    )}
                  </Stack>
                </TableCell>

                <TableCell align="right" onClick={e => e.stopPropagation()}>
                  <IconButton size="small" onClick={() => setModal(c)}>
                    <Edit fontSize="small" />
                  </IconButton>
                  <IconButton size="small" onClick={() => handleDelete(c.id)}>
                    <Delete fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </Paper>
)}

      {modal && (
        <ContactModal
          initial={modal}
          onClose={() => setModal(null)}
        />
      )}

      {selected && (
        <ContactDetail
          contact={selected}
          onClose={() => setSearchParams({})}
          onEdit={() => setModal(selected)}
          onDelete={() => handleDelete(selected.id)}
        />
      )}
    </Box>
  );
}